import TdShiftCodeButton from "@components/feature/rosterTable/TdShiftCodeButton";
import TdShiftCodeButtonView from "@components/feature/rosterTable/TdShiftCodeButtonView";
import type * as type from "@vTypes";

import RosterGrid from "@components/feature/roster_create/components/roster-grid.tsx";
import { cn } from "@utils/cn.ts";
import PerShiftStatsRow from "@components/feature/rosterTable/PerShiftStatsRow";
import PrevTailCells from "@components/feature/rosterTable/PrevTailCells";
import { memo, useMemo, type MouseEvent } from "react";
import useRosterConfig from "@/hooks/useRosterConfig";
import useStore from "@/store/store";
import type { PrevTailData } from "@/hooks/roster-craete/useRosterPrevTail";
import { NURSE_LIST_COLUMNS } from "@components/feature/rosterTable/script";
import type { CellViolationInfo } from "@/services/validation";
import type { InboundInfo } from "@/hooks/roster-craete/useRosterSchedule";
import {
  computeAssignmentStarts,
  computeAssignmentStartsExtended,
  computeInboundAssignmentStarts,
  inboundItemsFromInboundField,
  classifyInboundForGroup,
} from "@/services/assignment";
import type { PrevTailNurseAssignment } from "@/hooks/roster-craete/useRosterPrevTail";
import type { InboundItem } from "@/hooks/roster-craete/useRosterSchedule";
import {
  normalizeScheduleCellCode,
  resolveShiftFromScheduleCellFromMaps,
  resolveShiftFromScheduleCodeFromMaps,
  type ShiftLookupMaps,
} from "@/utils/shift-util";
import type { ShiftCatalogShift } from "@/services/shift/shiftCatalog";
import type {
  EmergencyBulkPeriodPick,
  EmergencyPostApplyHighlight,
  EmergencyReplacementSlotPick,
} from "@/types/emergency-replacement.types";
import { useRosterFocusStore } from "@/store/rosterFocusStore";
import NurseRowGrid from "@/components/nurse/nurseRowGrid";
import { RosterGridType } from "../roster_create/components/roster-grid-type";
import { useUser } from "@/hooks/useAuth";
import useGradeConfig from "@/hooks/useGradeConfig";
import { resolveGradeLabel } from "@/utils/grade-util";
import { useGroupStore } from "@/store/groupStore";
import { ChevronDown, ChevronUp } from "lucide-react";
import { MonthlyMembershipBadge } from "@/components/feature/nurse-membership/MonthlyMembershipContext";
import {
  getDailyResignationStatus,
  useMonthlyMembership,
} from "@/components/feature/nurse-membership/monthlyMembershipStore";
import { formatExperienceYears } from "@/utils/experience";
import type {
  RosterTableLayout,
  RosterTableLayoutConfig,
} from "@/components/nurse/rosterColumnHoverLayout";
import type { ShiftCodeStatusIndicator } from "@/pages/roster-create/components/shift-code/shift-code-cell";
import { resolveRosterAssignmentLock } from "../roster_create/utils/resolveRosterAssignmentLock";
import { isCurrentShiftBanned } from "@/pages/roster-create/utils/isCurrentShiftBanned";
import { shouldDimWantedReflectionCell } from "@/pages/roster-create/utils/wantedReflectionCellOpacity";
import type { GroupMonthlyMemo } from "@/components/feature/roster_create/utils/groupMonthlyMemo";
import { MonthlyMemoIndicator } from "./MonthlyMemoIndicator";

type NurseRuntimeFields = {
  nurse_id?: string;
  age?: number | string | null;
  grade?: number | null;
  /** 스케줄 API `schedule_ids` — 날짜 인덱스별 시프트 DB id, `availableShifts[].id`와 매칭 */
  schedule_ids?: (number | null)[] | null;
  isAppliedMap?: Record<number, boolean>;
  bannedShiftMap?: Record<
    number,
    {
      shiftIds: string[];
      isApplied: boolean;
      source?: string;
      reason?: string | null;
    }
  >;
  wantedEntriesMap?: Record<number, string>;
  /** 원티드 엔트리 `shifts_table_id` — `shiftLookupMaps.byNumericId`와 정합 */
  wantedShiftsTableIdsMap?: Record<number, number | null>;
  shiftMessages?: Record<number, string>;
};

function NurseRosterList({
  isEditMode,
  nursesIndex,
  nurse,
  isSubmitted,
  availableShifts,
  shiftLookupMaps,
  modDayShift,
  selectedShift,
  statsColumnShiftIds,
  statsDisplayShifts,
  selectIdForSideprofile,
  prevTailData,
  cellViolationMap,
  isEmergencyPickMode = false,
  onEmergencySlotPick,
  emergencySlotPick = null,
  isEmergencyBulkPickMode = false,
  onEmergencyBulkPick,
  emergencyBulkPeriodPick = null,
  emergencyBulkSelectedSteps = null,
  emergencyReplacementCandidateNurseId = null,
  emergencyPostApplyHighlight = null,
  scheduleYear = 0,
  scheduleMonth = 0,
  schedulePayloadYear,
  schedulePayloadMonth,
  showPerShiftStats = false,
  /** true: 발행 조회 — 읽기 전용 셀, 제출 배지 없음, 전월 tail 없음 */
  issuedRosterView = false,
  visibleShifts,
  roleGroupBottomBorder = false,
  dispatchGroupTopBorder = false,
  /** 원티드 조정판(모달): 위반 토글을 전역 대신 로컬로 — 미전달이면 일반 근무표 행 */
  violationVisibilityOverride,
  /** false: 셀 클릭으로 열·행 선택(클릭 하이라이트) 안 함 — 호버만 */
  selectionFocusOnClick = true,
  allowAssignmentLockedCellInteraction = false,
  currentGroupId,
  showRowReorder = false,
  canMoveRowUp = false,
  canMoveRowDown = false,
  onMoveRowUp,
  onMoveRowDown,
  layout = "create",
  layoutConfig,
  hideLevelColumn = false,
  monthlyMemoByNurse,
  displayRowNumber,
}: {
  isEditMode: boolean;
  nursesIndex: number;
  displayRowNumber?: number;
  issuedRosterView: boolean;
  nurse: {
    counts?: { [key: string]: number | null } | null;
    experience?: number;
    id?: string;
    nurse_id?: string;
    name: string;
    age?: number | string | null;
    grade?: number | null;
    preceptor?: {
      color: string;
      ee: string[];
      group: { preceptor: string; preceptee: string[] } | number;
      groupIndex?: number;
      or: string[];
      role: string;
    } | null;
    schedule?: (string | null | { code: string | null })[] | null;
    off?: number;
    level?: string | null;
    level_?: string | null;
    role: string;
    shiftMessages?: Record<number, string>; // 날짜별 메시지 (comment)
    is_inbound?: boolean;
    inbound?: InboundInfo | InboundItem[];
    /** 스케줄 API·발행 병합 행 — `inbound`(배열 또는 `{ inbound_list }`)·기간 바에 사용 */
    assignments?: InboundItem[];
  };
  isSubmitted: boolean;
  availableShifts: type.availableShifts[];
  /** `mergeShiftsForCellLookup` + `buildShiftLookupMaps` — 상위에서 `useShiftLookupMaps`로 생성 */
  shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift>;
  modDayShift: (
    nurseId: string,
    scheduleDay: number,
    changeShift: string,
    cellShiftDbId?: number | null
  ) => void;
  selectedShift: type.Type_availableShifts | null;

  /** 헤더 통계 칩과 동일 열 순서( canonical shift_id ) */
  statsColumnShiftIds?: string[];
  /** 동일 shift_id가 여러 행일 때 DB id로 열·집계 구분 */
  statsDisplayShifts?: type.availableShifts[];
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  disableManpowerValidation?: boolean; // 최소인원 검증 비활성화 옵션
  violations?: unknown[]; // 위반사항 목록
  copyNurses?: unknown[]; // 전체 간호사 목록 (그룹 합계 계산용)
  prevTailData?: PrevTailData | null; // 전월 말일 근무표 데이터
  cellViolationMap?: Map<string, CellViolationInfo>;
  isEmergencyPickMode?: boolean;
  onEmergencySlotPick?: (pick: EmergencyReplacementSlotPick) => void;
  emergencySlotPick?: EmergencyReplacementSlotPick | null;
  isEmergencyBulkPickMode?: boolean;
  onEmergencyBulkPick?: (pick: {
    nurseId: string;
    nurseName: string;
    dayIndex: number;
    dateISO: string;
    shiftColor?: string;
  }) => void;
  emergencyBulkPeriodPick?: EmergencyBulkPeriodPick | null;
  emergencyBulkSelectedSteps?: Array<{ date: string; nurseId: string }> | null;
  emergencyReplacementCandidateNurseId?: string | null;
  emergencyPostApplyHighlight?: EmergencyPostApplyHighlight | null;
  scheduleYear?: number;
  scheduleMonth?: number;
  /** 스케줄 API 응답의 year/month — 뷰와 일치할 때만 `inbound` 항목으로 기간 바 표시 */
  schedulePayloadYear?: number;
  schedulePayloadMonth?: number;
  /** false면 행 우측 ShiftStatus(근무코드별 집계) 숨김 */
  showPerShiftStats?: boolean;
  /** RN/AN/ETC 등 직군 그룹이 다음 행과 바뀔 때 하단 구분선 */
  roleGroupBottomBorder?: boolean;
  dispatchGroupTopBorder?: boolean;
  visibleShifts?: string[];
  /** 원티드 조정판 등: 전달 시 위반 표시·원티드 mismatch UX를 조정판 모드로 */
  violationVisibilityOverride?: {
    showConfigViolations: boolean;
    showManpowerViolations: boolean;
  } | null;
  selectionFocusOnClick?: boolean;
  allowAssignmentLockedCellInteraction?: boolean;
  /** 현재 편집 중인 병동 group_id — inbound 잠금 정책에 사용 */
  currentGroupId?: string | null;
  showRowReorder?: boolean;
  canMoveRowUp?: boolean;
  canMoveRowDown?: boolean;
  onMoveRowUp?: () => void;
  onMoveRowDown?: () => void;
  layout?: RosterTableLayout;
  layoutConfig?: RosterTableLayoutConfig;
  hideLevelColumn?: boolean;
  monthlyMemoByNurse?: ReadonlyMap<string, GroupMonthlyMemo>;
}) {
  const nurseRuntime = nurse as unknown as NurseRuntimeFields;
  const isWantedAdjustmentBoard = violationVisibilityOverride != null;
  const shouldShowLevelColumn = !hideLevelColumn;
  const rawLevelLabel = nurse.level_ ?? nurse.level;
  const levelLabel = rawLevelLabel?.trim() || "-";
  const nurseId = nurseRuntime.nurse_id || nurse.id;
  const nurseIdAsString = String(nurseId);
  const monthlyMemo = monthlyMemoByNurse?.get(nurseIdAsString);
  const monthlyMembership = useMonthlyMembership(nurseIdAsString);
  const user = useUser();
  const { selectedGroupId: selectedGroupIdFromStore } = useGroupStore();
  const gradeConfigGroupId = currentGroupId ?? selectedGroupIdFromStore ?? null;
  const { data: gradeData } = useGradeConfig({
    selectedGroupId: gradeConfigGroupId,
  });

  const isMyData = user?.nurse_id === nurseId;

  /**
   * 그룹 기준 잠금 분류는 `inbound`를 우선 사용하고,
   * 일부 응답에서 `inbound`가 비어 있는 행은 `assignments`를 fallback으로 사용한다.
   */
  // 프리셉티(reason='프리셉티')는 파견/병동이동이 아니므로 파견 잠금 분류에서 제외한다.
  // (nurse_assignment 에 kind='preceptee', target_group_id=NULL 로 저장되어
  //  classifyInboundForGroup 이 outbound 파견으로 오인 → 셀 잠금되는 문제 차단)
  const lockableAssignments = useMemo(
    () => (nurse.assignments ?? []).filter((a) => a.reason !== "프리셉티"),
    [nurse.assignments]
  );

  const inboundForGroupClassification = useMemo(() => {
    const inboundItems = inboundItemsFromInboundField(nurse.inbound).filter(
      (a) => a.reason !== "프리셉티"
    );
    if (inboundItems.length > 0) return inboundItems;
    return lockableAssignments;
  }, [nurse.inbound, lockableAssignments]);

  const { lockedDays, lockedSegments } = useMemo(() => {
    const sy = scheduleYear ?? 0;
    const sm = scheduleMonth ?? 0;
    if (!sy || !sm)
      return { lockedDays: new Set<number>(), lockedSegments: [] };
    const dim = new Date(sy, sm, 0).getDate();
    return classifyInboundForGroup(
      inboundForGroupClassification,
      currentGroupId,
      dim,
      sy,
      sm
    );
  }, [
    inboundForGroupClassification,
    currentGroupId,
    scheduleYear,
    scheduleMonth,
  ]);

  // prev tail 대상 간호사의 assignments 추출 (레거시 nurse_assignments fallback)
  const prevNurseAssignments = useMemo((): PrevTailNurseAssignment[] => {
    if (!prevTailData?.nurses) return [];
    const currentNurseId = String(nurseId);
    const prevNurse = prevTailData.nurses.find(
      (n) => String(n.nurse_id) === currentNurseId
    );
    const row = prevNurse as
      | (typeof prevNurse & {
          assignments?: PrevTailNurseAssignment[];
          nurse_assignments?: PrevTailNurseAssignment[];
        })
      | undefined;
    return (row?.assignments ?? row?.nurse_assignments ?? []).filter(
      (a) => a.reason !== "프리셉티"
    );
  }, [prevTailData, nurseId]);

  // prev tail 일자 배열 (정렬된 숫자)
  const prevTailDays = useMemo((): number[] => {
    if (!prevTailData) return [];
    const src =
      prevTailData.tail_days?.length > 0
        ? prevTailData.tail_days
        : prevTailData.nurses
          ? (() => {
              const currentNurseId = String(nurseId);
              const prevNurse = prevTailData.nurses.find(
                (n) => String(n.nurse_id) === currentNurseId
              );
              return prevNurse?.shifts
                ? Object.keys(prevNurse.shifts).map(Number)
                : [];
            })()
          : [];
    return [...new Set(src.map(Number).filter((d) => d >= 1 && d <= 31))].sort(
      (a, b) => a - b
    );
  }, [prevTailData, nurseId]);

  // dedup 병합: nurse.assignments(현재월) + prevNurseAssignments(전월)
  const mergedAssignmentItems =
    useMemo((): import("@/hooks/roster-craete/useRosterSchedule").InboundItem[] => {
      const keyOf = (item: {
        reason: string;
        target_group_id?: string;
        startDate?: string;
        start_date?: string;
        endDate?: string | null;
        end_date?: string | null;
      }) =>
        `${item.reason}|${item.target_group_id ?? ""}|${item.startDate ?? item.start_date ?? ""}|${item.endDate ?? item.end_date ?? ""}`;

      const seen = new Set<string>();
      const out: import("@/hooks/roster-craete/useRosterSchedule").InboundItem[] =
        [];

      for (const item of lockableAssignments) {
        const k = keyOf(item);
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(item);
      }
      for (const item of prevNurseAssignments) {
        const k = keyOf(item);
        if (seen.has(k)) continue;
        seen.add(k);
        const startDate = item.start_date;
        const endDate = item.end_date;
        out.push({
          startDate,
          endDate,
          reason: item.reason,
          target_group_id: item.target_group_id,
          target_group_name: item.target_group_name,
        });
      }
      return out;
    }, [lockableAssignments, prevNurseAssignments]);

  // tail 포함 extended 좌표계로 계산 (startDay 음수 = tail, 양수 = 현재월)
  const mergedAssignmentStarts = useMemo(() => {
    const sy = scheduleYear ?? 0;
    const sm = scheduleMonth ?? 0;
    if (!sy || !sm) return [];
    const py = schedulePayloadYear;
    const pm = schedulePayloadMonth;
    const payloadMatchesView =
      py != null &&
      pm != null &&
      Number.isFinite(py) &&
      Number.isFinite(pm) &&
      py === sy &&
      pm === sm;

    if (!payloadMatchesView && prevTailDays.length === 0) return [];

    const items = payloadMatchesView
      ? mergedAssignmentItems
      : prevTailDays.length > 0
        ? mergedAssignmentItems
        : [];
    if (items.length === 0 && prevTailDays.length === 0) return [];

    return computeAssignmentStartsExtended(
      items,
      sy,
      sm,
      prevTailDays.length > 0 ? prevTailDays : undefined
    );
  }, [
    mergedAssignmentItems,
    prevTailDays,
    schedulePayloadYear,
    schedulePayloadMonth,
    scheduleYear,
    scheduleMonth,
  ]);

  // tail 시작점을 가진 dedupKey 집합 (현재월 렌더에서 스킵용)
  const tailStartedDedupKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const s of mergedAssignmentStarts) {
      if (s.startDay < 0 && s.dedupKey) keys.add(s.dedupKey);
    }
    return keys;
  }, [mergedAssignmentStarts]);

  // 전월 tail에서 시작해 현재월로 이어진 구간 메타.
  // 같은(reason/target) 현재월 시작 인디케이터는 tail 기준으로만 보이도록 숨긴다.
  const tailContinuationWindows = useMemo(() => {
    const windows: Array<{
      reason: string;
      targetGroupId?: string;
      startDay: number;
      endDay: number;
    }> = [];

    for (const s of mergedAssignmentStarts) {
      if (s.startDay >= 0) continue;
      // extended rangeLength 규칙: start<0, end>=1 인 경우 range = (-start) + end
      const currentMonthEnd = s.rangeLength + s.startDay;
      if (currentMonthEnd < 1) continue;
      windows.push({
        reason: s.reason,
        targetGroupId: s.targetGroupId,
        startDay: 1,
        endDay: currentMonthEnd,
      });
    }

    return windows;
  }, [mergedAssignmentStarts]);

  const nurseAssignmentStarts = useMemo(() => {
    const sy = scheduleYear ?? 0;
    const sm = scheduleMonth ?? 0;
    const py = schedulePayloadYear;
    const pm = schedulePayloadMonth;
    const payloadMatchesView =
      py != null &&
      pm != null &&
      Number.isFinite(py) &&
      Number.isFinite(pm) &&
      py === sy &&
      pm === sm;

    // lockedSegments 기반: outbound / outsider 잠금 바 포함
    const segmentStarts = lockedSegments;

    const params = {
      scheduleYear: sy,
      scheduleMonth: sm,
      schedulePayloadYear,
      schedulePayloadMonth,
    };

    // currentGroupId가 있으면 lockedSegments가 inbound를 대체
    const inboundStarts = currentGroupId
      ? []
      : computeInboundAssignmentStarts(nurse.inbound, params);

    // 현재월 assignments: tail 시작점인 배정은 mergedAssignmentStarts 에서 처리하므로 스킵
    const assignItems = (nurse.assignments ?? []).filter((item) => {
      if (tailStartedDedupKeys.size === 0) return true;
      const k = `${item.reason}|${item.target_group_id ?? item.targetGroupId ?? ""}|${item.startDate ?? ""}|${item.endDate ?? ""}`;
      return !tailStartedDedupKeys.has(k);
    });
    const fromAssignments =
      payloadMatchesView && assignItems.length > 0
        ? computeAssignmentStarts(assignItems, sy, sm)
        : [];

    const isCoveredByTailContinuation = (s: {
      reason: string;
      startDay: number;
      targetGroupId?: string;
    }) => {
      if (s.startDay < 1) return false;
      return tailContinuationWindows.some((w) => {
        if (w.reason !== s.reason) return false;
        const sameTarget = (w.targetGroupId ?? "") === (s.targetGroupId ?? "");
        if (!sameTarget) return false;
        return s.startDay >= w.startDay && s.startDay <= w.endDay;
      });
    };

    // 세 소스 병합: lockedSegments > fromAssignments > inboundStarts (startDay 키)
    const byDay = new Map<number, (typeof inboundStarts)[number]>();
    for (const s of inboundStarts) {
      if (isCoveredByTailContinuation(s)) continue;
      byDay.set(s.startDay, s);
    }
    for (const s of fromAssignments) {
      if (isCoveredByTailContinuation(s)) continue;
      byDay.set(s.startDay, s);
    }
    // mergedAssignmentStarts 중 현재월 시작 항목 포함 (tail 시작 항목은 위 filter로 이미 제외됨)
    for (const s of mergedAssignmentStarts) {
      if (isCoveredByTailContinuation(s)) continue;
      if (s.startDay >= 1) byDay.set(s.startDay, s);
    }
    for (const s of segmentStarts) {
      if (isCoveredByTailContinuation(s)) continue;
      byDay.set(s.startDay, s);
    }
    return [...byDay.values()].sort((a, b) => a.startDay - b.startDay);
  }, [
    lockedSegments,
    currentGroupId,
    nurse.inbound,
    nurse.assignments,
    schedulePayloadYear,
    schedulePayloadMonth,
    scheduleYear,
    scheduleMonth,
    tailStartedDedupKeys,
    mergedAssignmentStarts,
    tailContinuationWindows,
  ]);
  const assignmentLockedDays = useMemo(() => {
    if (!isWantedAdjustmentBoard) return new Set<number>();
    const sy = scheduleYear ?? 0;
    const sm = scheduleMonth ?? 0;
    const py = schedulePayloadYear;
    const pm = schedulePayloadMonth;
    const payloadMatchesView =
      py != null &&
      pm != null &&
      Number.isFinite(py) &&
      Number.isFinite(pm) &&
      py === sy &&
      pm === sm;
    if (!payloadMatchesView) return new Set<number>();
    const assignItems = mergedAssignmentItems;
    if (assignItems.length === 0) return new Set<number>();
    const starts = computeAssignmentStarts(assignItems, sy, sm);
    const locked = new Set<number>();
    for (const s of starts) {
      const end = s.startDay + s.rangeLength - 1;
      for (let d = s.startDay; d <= end; d++) {
        locked.add(d);
      }
    }
    return locked;
  }, [
    isWantedAdjustmentBoard,
    mergedAssignmentItems,
    schedulePayloadYear,
    schedulePayloadMonth,
    scheduleYear,
    scheduleMonth,
  ]);
  const effectiveLockedDays = useMemo(() => {
    if (assignmentLockedDays.size === 0) return lockedDays;
    const merged = new Set<number>(lockedDays);
    for (const d of assignmentLockedDays) merged.add(d);
    return merged;
  }, [lockedDays, assignmentLockedDays]);

  /**
   * 파견·휴직·inbound 등 배정 바(`AssignmentGroupBar` width)가 당월에 걸친 모든 일자.
   * 원티드 반영사항 보기 딤(opacity-50)은 이 구간·잠금일에 적용하지 않는다.
   */
  const daysCoveredByAssignmentBars = useMemo(() => {
    const sy = scheduleYear ?? 0;
    const sm = scheduleMonth ?? 0;
    if (!sy || !sm) return new Set<number>();
    const dim = new Date(sy, sm, 0).getDate();
    const out = new Set<number>();
    for (const seg of nurseAssignmentStarts) {
      if (seg.startDay < 1) continue;
      const len = seg.rangeLength ?? 1;
      const end = Math.min(seg.startDay + len - 1, dim);
      for (let d = seg.startDay; d <= end; d++) {
        if (d >= 1) out.add(d);
      }
    }
    return out;
  }, [nurseAssignmentStarts, scheduleYear, scheduleMonth]);

  const isRowHovered = useRosterFocusStore(
    (state) => state.hoveredNurseId === nurseIdAsString
  );
  const isRowSelected = useRosterFocusStore(
    (state) => state.selectedNurseId === nurseIdAsString
  );
  const isPinnedRowSelected = useRosterFocusStore(
    (state) =>
      state.isSelectedFocusPinned && state.selectedNurseId === nurseIdAsString
  );

  const { data: serverConfig } = useRosterConfig();
  const showLevel = serverConfig?.show_level;
  const showPreceptor = serverConfig?.show_preceptor;
  const {
    showWantedViolations,
    showManpowerViolations: storeManpower,
    showConfigViolations: storeConfig,
  } = useStore();

  const showManpowerViolations =
    violationVisibilityOverride?.showManpowerViolations ?? storeManpower;
  const showConfigViolations =
    violationVisibilityOverride?.showConfigViolations ?? storeConfig;

  const preceptorGroupKey =
    nurse.preceptor == null
      ? 0
      : (nurse.preceptor.groupIndex ??
        (typeof nurse.preceptor.group === "number"
          ? nurse.preceptor.group
          : 0));

  function eventPreceptorEnter(num: number) {
    document
      .querySelectorAll(`.preceptorGroup${num}`)
      .forEach((tr) => tr.classList.add("hover"));
  }
  function eventPreceptorLeave(num: number) {
    document
      .querySelectorAll(`.preceptorGroup${num}`)
      .forEach((tr) => tr.classList.remove("hover"));
  }
  function eventPreceptorClick(num: number) {
    document
      .querySelectorAll(`.preceptorGroup:not(.preceptorGroup${num}).click`)
      .forEach((tr) => tr.classList.remove("click"));
    document
      .querySelectorAll(`.preceptorGroup${num}`)
      .forEach((tr) => tr.classList.toggle("click"));
  }

  function handleMoveRowUp(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (!canMoveRowUp) return;
    onMoveRowUp?.();
  }

  function handleMoveRowDown(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (!canMoveRowDown) return;
    onMoveRowDown?.();
  }

  function getStatusIndicator(
    dayNumber: number
  ): ShiftCodeStatusIndicator | null {
    const status = getDailyResignationStatus(
      monthlyMembership,
      scheduleYear,
      scheduleMonth,
      dayNumber
    );
    if (!status) return null;

    const startDay = Number(status.startDate.slice(8, 10));
    const monthEndDay = new Date(scheduleYear, scheduleMonth, 0).getDate();
    const rangePosition =
      startDay === monthEndDay
        ? "single"
        : dayNumber === startDay
          ? "start"
          : dayNumber === monthEndDay
            ? "end"
            : "middle";

    return {
      reason: status.reason,
      label: "퇴사",
      title: `퇴사일: ${status.startDate}`,
      rangePosition,
    };
  }

  return (
    <NurseRowGrid
      nurseId={nurseIdAsString}
      isRowSelected={isRowSelected}
      isPinnedRowSelected={isPinnedRowSelected}
      isRowHovered={isRowHovered}
      roleGroupBottomBorder={roleGroupBottomBorder}
      dispatchGroupTopBorder={dispatchGroupTopBorder}
    >
      <div className="flex gap-[10px] font-pretendard">
        <RosterGrid
          className={cn(
            nurse.preceptor && "preceptorGroup",
            nurse.preceptor && `preceptorGroup${preceptorGroupKey}`
          )}
          hasPrevTail={issuedRosterView ? false : !!prevTailData}
          gridType={RosterGridType.BODY}
          layout={layout}
          layoutConfig={layoutConfig}
        >
          <div
            className={cn(
              "flex w-full min-w-0 flex-1 flex-shrink-0 items-center justify-start gap-0 "
            )}
          >
            <div
              className="rt_nurse_moveHandler flex-shrink-0 flex justify-center"
              style={{ width: NURSE_LIST_COLUMNS.handle }}
            >
              <div className="flex w-full items-center justify-center gap-1.5">
                {showRowReorder && (
                  <div className="flex flex-col items-center justify-center">
                    <button
                      type="button"
                      className="flex size-3.5 items-center justify-center rounded text-[#8A8A93] transition-colors hover:bg-[#EEF3FF] hover:text-sig disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#8A8A93]"
                      aria-label={`${nurse.name} 위로 이동`}
                      title="위로 이동"
                      disabled={!canMoveRowUp}
                      onClick={handleMoveRowUp}
                    >
                      <ChevronUp className="size-3" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="flex size-3.5 items-center justify-center rounded text-[#8A8A93] transition-colors hover:bg-[#EEF3FF] hover:text-sig disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#8A8A93]"
                      aria-label={`${nurse.name} 아래로 이동`}
                      title="아래로 이동"
                      disabled={!canMoveRowDown}
                      onClick={handleMoveRowDown}
                    >
                      <ChevronDown className="size-3" aria-hidden="true" />
                    </button>
                  </div>
                )}
                <span
                  className={cn(
                    "min-w-4 text-[12px] text-[#666666] text-center",
                    isMyData && "text-sig font-semibold"
                  )}
                >
                  {displayRowNumber ?? nursesIndex + 1}
                </span>
              </div>
            </div>

            <div
              className=" flex-shrink-0 flex items-center justify-center gap-1"
              style={{ width: NURSE_LIST_COLUMNS.name }}
            >
              <div
                className={cn(
                  "flex min-w-0 items-center gap-1",
                  issuedRosterView && "justify-center"
                )}
              >
                {!issuedRosterView && (
                  <div
                    className={`submitMark ${isSubmitted && "submitted"} hoverMessageWrap`}
                  >
                    <div className="hoverMessage">
                      {isSubmitted ? "제출완료" : "미제출"}
                    </div>
                  </div>
                )}

                <div
                  className={cn(
                    "rt_nurse_name_block_name min-w-0 truncate",
                    !issuedRosterView &&
                      "cursor-pointer hover:opacity-70 transition-opacity",
                    isMyData && "text-sig! font-semibold"
                  )}
                  onClick={() => {
                    if (
                      !issuedRosterView &&
                      selectIdForSideprofile &&
                      nurseId
                    ) {
                      selectIdForSideprofile(true, nurseId);
                    }
                  }}
                >
                  {nurse.name}
                </div>
                <MonthlyMemoIndicator nurseName={nurse.name} memo={monthlyMemo} />
                <MonthlyMembershipBadge nurseId={nurseIdAsString} />
              </div>
            </div>

            {/* 프리셉터 */}
            {showPreceptor && (
              <div
                className="flex-shrink-0 flex justify-center items-center"
                style={{ width: NURSE_LIST_COLUMNS.preceptor }}
              >
                <div className=" rt_nurse_name_gridWrap flex justify-center items-center">
                  {nurse.preceptor ? (
                    <button
                      type="button"
                      className={`preceptorMark hoverMessageWrap size-2.5! ${
                        nurse.preceptor.role !== "preceptor" ? "border-3" : ""
                      }`}
                      style={{
                        backgroundColor: `rgb(${nurse.preceptor.color})`,
                        ...(nurse.preceptor.role !== "preceptor"
                          ? {
                              borderColor: `rgb(${nurse.preceptor.color})`,
                              backgroundColor: "transparent",
                            }
                          : {}),
                      }}
                      onMouseEnter={() => {
                        if (nurse.preceptor) {
                          eventPreceptorEnter(preceptorGroupKey);
                        }
                      }}
                      onMouseLeave={() => {
                        if (nurse.preceptor) {
                          eventPreceptorLeave(preceptorGroupKey);
                        }
                      }}
                      onClick={() => {
                        if (nurse.preceptor) {
                          eventPreceptorClick(preceptorGroupKey);
                        }
                      }}
                    >
                      <div className="hoverMessage">
                        {nurse.preceptor.role === "preceptor"
                          ? `프리셉티 ${nurse.preceptor.ee.map((text) => text)}`
                          : `프리셉터 ${nurse.preceptor.or.map((text) => text)}`}
                      </div>
                    </button>
                  ) : (
                    <span className="text-[#939399]">-</span>
                  )}
                </div>
              </div>
            )}

            {/* 나이 */}
            {/* <div
              className="rt_nurse_role flex-shrink-0 text-center"
              style={{ color: "#939399", width: NURSE_LIST_COLUMNS.age }}
            >
              <div className="">
                <div className={cn(isMyData && "text-sig! font-semibold")}>
                  {nurse?.age ?? "-"}
                </div>
              </div>
            </div> */}

            {/* Grade */}
            {layoutConfig?.showGradeColumn !== false && (
              <div
                className="rt_nurse_role flex-shrink-0 text-center"
                style={{ color: "#939399", width: NURSE_LIST_COLUMNS.grade }}
              >
                <div className="">
                  <div
                    className={cn(
                      "flex items-center justify-center",
                      isMyData && "text-sig! font-semibold "
                    )}
                  >
                    <span className="truncate max-w-[72px]! overflow-hidden! inline-block">
                      {nurse.grade && nurse.grade !== 0
                        ? resolveGradeLabel(
                            nurse.grade,
                            gradeData?.gradeSelectOptions,
                            gradeData?.grade_names ?? undefined
                          )
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* 직군 */}
            {showLevel && shouldShowLevelColumn && (
              <div
                className="rt_nurse_role flex-shrink-0 text-center"
                style={{ color: "#939399", width: NURSE_LIST_COLUMNS.role }}
              >
                <div className="">
                  <div className={cn(isMyData && "text-sig! font-semibold")}>
                    <div className="whitespace-nowrap overflow-hidden text-ellipsis">
                      {nurse.role}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showLevel && shouldShowLevelColumn && (
              <div
                className="rt_nurse_role flex-shrink-0 text-center"
                style={{ color: "#939399", width: NURSE_LIST_COLUMNS.level }}
              >
                <div className={cn(isMyData && "text-sig! font-semibold")}>
                  <div
                    className="whitespace-nowrap overflow-hidden text-ellipsis"
                    title={levelLabel !== "-" ? levelLabel : undefined}
                  >
                    {levelLabel}
                  </div>
                </div>
              </div>
            )}

            {/* 경력 */}
            {layoutConfig?.showExperienceColumn !== false && (
              <div
                className="rt_nurse_role flex-shrink-0 text-center"
                style={{ color: "#939399", width: NURSE_LIST_COLUMNS.experience }}
              >
                <div className={cn(isMyData && "text-sig! font-semibold")}>
                  <div className="">{formatExperienceYears(nurse.experience)}</div>
                </div>
              </div>
            )}
          </div>
          {/* Schedule*/}
          <div
            className={cn(
              "flex relative",
              "after:absolute after:inset-y-0 after:border-r after:border-dotted after:border-[#D6D6DE] after:w-0 after:content-[''] after:-right-[3px]"
            )}
            data-roster-schedule-row=""
          >
            {!issuedRosterView ? (
              <PrevTailCells
                prevTailData={prevTailData}
                nurseId={String(nurseId)}
                nursesIndex={nursesIndex}
                isEditMode={isEditMode}
                shiftLookupMaps={shiftLookupMaps}
                cellViolationMap={cellViolationMap}
                assignmentStarts={mergedAssignmentStarts}
                showManpowerViolations={showManpowerViolations}
                showConfigViolations={showConfigViolations}
              />
            ) : null}

            <div className="flex items-center   ">
              {/* 현재 월 스케줄 */}
              {issuedRosterView
                ? nurse.schedule?.map((code, index) => {
                    const cellCode = normalizeScheduleCellCode(code);
                    const numericId = nurseRuntime.schedule_ids?.[index];
                    const resolved = resolveShiftFromScheduleCellFromMaps(
                      cellCode,
                      numericId ?? null,
                      shiftLookupMaps
                    );
                    const shift = resolved
                      ? (availableShifts.find(
                          (item) => item.id === resolved.id
                        ) ?? resolved)
                      : undefined;
                    const shiftForView = shift
                      ? {
                          ...shift,
                          name: shift.name ?? shift.shift_id,
                          color: shift.color ?? "#F0F0F2",
                          default_shift: shift.default_shift ?? undefined,
                          shift_gb: shift.shift_gb ?? undefined,
                        }
                      : undefined;
                    const assignmentStart =
                      nurseAssignmentStarts.find(
                        (s) => s.startDay === index + 1
                      ) ?? null;
                    const statusIndicator = getStatusIndicator(index + 1);
                    return (
                      <TdShiftCodeButtonView
                        key={index}
                        day={index + 1}
                        shift={shiftForView}
                        nurseId={nurseIdAsString}
                        nurseName={nurse.name}
                        nursesIndex={nursesIndex}
                        assignmentStart={assignmentStart}
                        statusIndicator={statusIndicator}
                      />
                    );
                  })
                : nurse.schedule?.map((code, index) => {
                    const cellCode = normalizeScheduleCellCode(code);
                    const numericId = nurseRuntime.schedule_ids?.[index];

                    const shift = resolveShiftFromScheduleCellFromMaps(
                      cellCode,
                      numericId ?? null,
                      shiftLookupMaps
                    );

                    const shiftForTd = shift
                      ? {
                          ...shift,
                          name: shift.name ?? shift.shift_id,
                          color: shift.color ?? "#F0F0F2",
                          shift_gb: shift.shift_gb ?? undefined,
                        }
                      : undefined;
                    const cellKey = `${nurseId}-${index}`;
                    const cellInfo = cellViolationMap?.get(cellKey);
                    const isError = cellInfo?.isError ?? false;
                    const violationGroups = cellInfo?.groups ?? [];
                    const violationMessagesForTooltip =
                      cellInfo?.messages ?? [];
                    const violationTypedMessagesForTooltip =
                      cellInfo?.typedMessages ?? [];

                    const assignmentStart =
                      nurseAssignmentStarts.find(
                        (s) => s.startDay === index + 1
                      ) ?? null;
                    const statusIndicator = getStatusIndicator(index + 1);

                    const dayNumber = index + 1;
                    const comment = nurseRuntime.shiftMessages?.[dayNumber];
                    const isApplied =
                      nurseRuntime.isAppliedMap?.[dayNumber] ?? undefined;
                    const bannedShift = nurseRuntime.bannedShiftMap?.[dayNumber];
                    const bannedShiftCatalog = bannedShift?.shiftIds[0]
                      ? resolveShiftFromScheduleCodeFromMaps(
                          bannedShift.shiftIds[0],
                          shiftLookupMaps
                        )
                      : undefined;
                    const wantedShiftId =
                      nurseRuntime.wantedEntriesMap?.[dayNumber];
                    const wantedTableId =
                      nurseRuntime.wantedShiftsTableIdsMap?.[dayNumber];
                    const currentShiftId = shift?.shift_id ?? cellCode;
                    const bannedWantedDisplayContext = isWantedAdjustmentBoard
                      ? "adjustment"
                      : showWantedViolations
                        ? "reflection"
                        : undefined;
                    const shouldEvaluateBannedWanted =
                      showWantedViolations || isWantedAdjustmentBoard;
                    const isBannedShiftViolation =
                      shouldEvaluateBannedWanted &&
                      bannedShift?.isApplied === true &&
                      isCurrentShiftBanned(
                        currentShiftId,
                        bannedShift.shiftIds
                      );

                    const shouldSkipWantedMismatchForInbound =
                      effectiveLockedDays.has(dayNumber) ||
                      daysCoveredByAssignmentBars.has(dayNumber);

                    let isWantedMatch = false;
                    let isWantedMismatch = false;
                    if (
                      wantedShiftId &&
                      (isWantedAdjustmentBoard || showWantedViolations)
                    ) {
                      const resolvedWanted =
                        resolveShiftFromScheduleCellFromMaps(
                          wantedShiftId,
                          wantedTableId ?? null,
                          shiftLookupMaps
                        );
                      const resolvedCurrent =
                        shift ??
                        resolveShiftFromScheduleCodeFromMaps(
                          currentShiftId,
                          shiftLookupMaps
                        );
                      if (resolvedWanted && resolvedCurrent) {
                        isWantedMismatch =
                          resolvedWanted.shift_id !== resolvedCurrent.shift_id;
                        isWantedMatch = !isWantedMismatch;
                      }
                    }
                    if (shouldSkipWantedMismatchForInbound) {
                      isWantedMatch = false;
                      isWantedMismatch = false;
                    }
                    const shouldApplyOpacity = shouldDimWantedReflectionCell({
                      isBannedShiftViolation,
                      isCellValidationError: isError,
                      isWantedAdjustmentBoard,
                      isWantedMatch,
                      isWantedMismatch,
                      isWantedReflectionVisible: showWantedViolations,
                      isWithinInboundLock: shouldSkipWantedMismatchForInbound,
                    });

                    return (
                      <div
                        key={index}
                        className={cn(
                          shouldApplyOpacity ? "opacity-50" : "opacity-100",
                          "transition-opacity duration-300"
                        )}
                      >
                        <TdShiftCodeButton
                          isEveryFifth={!((index + 1) % 5)}
                          nurse_id={nurseIdAsString}
                          nursesIndex={nursesIndex}
                          isFirstNurseRow={nursesIndex === 0}
                          day={index + 1}
                          shift={shiftForTd}
                          modDayShift={modDayShift}
                          selectedShift={selectedShift}
                          isError={isError}
                          violationGroup={
                            violationGroups.length > 0
                              ? violationGroups[0]
                              : null
                          }
                          violationGroups={
                            violationGroups.length > 0 ? violationGroups : null
                          }
                          assignmentStart={assignmentStart}
                          statusIndicator={statusIndicator}
                          violationMessages={violationMessagesForTooltip}
                          violationTypedMessages={
                            violationTypedMessagesForTooltip
                          }
                          comment={comment}
                          isApplied={isApplied}
                          bannedShiftIds={bannedShift?.shiftIds}
                          bannedShiftColor={bannedShiftCatalog?.color}
                          isBannedApplied={bannedShift?.isApplied}
                          isBannedShiftViolation={isBannedShiftViolation}
                          bannedWantedDisplayContext={
                            bannedWantedDisplayContext
                          }
                          bannedShiftSource={bannedShift?.source}
                          bannedReason={bannedShift?.reason ?? undefined}
                          isWantedMatch={isWantedMatch}
                          isWantedMismatch={isWantedMismatch}
                          wantedShiftId={wantedShiftId}
                          cellScheduleCode={cellCode}
                          isEmergencyPickMode={isEmergencyPickMode}
                          onEmergencySlotPick={onEmergencySlotPick}
                          emergencySlotPick={emergencySlotPick}
                          isEmergencyBulkPickMode={isEmergencyBulkPickMode}
                          onEmergencyBulkPick={onEmergencyBulkPick}
                          emergencyBulkPeriodPick={emergencyBulkPeriodPick}
                          emergencyBulkSelectedSteps={
                            emergencyBulkSelectedSteps
                          }
                          emergencyReplacementCandidateNurseId={
                            emergencyReplacementCandidateNurseId
                          }
                          emergencyPostApplyHighlight={
                            emergencyPostApplyHighlight
                          }
                          nurseName={nurse.name}
                          scheduleYear={scheduleYear}
                          scheduleMonth={scheduleMonth}
                          violationVisibilityOverride={
                            isWantedAdjustmentBoard
                              ? {
                                  showConfigViolations,
                                  showManpowerViolations,
                                }
                              : undefined
                          }
                          isEditModeOverride={
                            isWantedAdjustmentBoard ? isEditMode : undefined
                          }
                          selectionFocusOnClick={selectionFocusOnClick}
                          isAssignmentLocked={resolveRosterAssignmentLock({
                            isAssignmentLocked: effectiveLockedDays.has(
                              index + 1
                            ),
                            allowAssignmentLockedCellInteraction,
                          })}
                        />
                      </div>
                    );
                  })}
            </div>
          </div>
        </RosterGrid>
        <div className="size-10 shrink-0 rounded-full" aria-hidden />
      </div>
      {/* DENO - Status 분리함.. */}
      {showPerShiftStats && (
        <PerShiftStatsRow
          className={issuedRosterView ? "ml-0" : undefined}
          nurseSchedule={nurse.schedule ?? []}
          scheduleIds={nurseRuntime.schedule_ids ?? undefined}
          shiftDefinitions={availableShifts}
          visibleShifts={visibleShifts}
          columnShiftIds={statsColumnShiftIds}
          columnShifts={statsDisplayShifts}
          shiftLookupMaps={shiftLookupMaps}
          counts={nurse.counts ?? null}
        />
      )}
    </NurseRowGrid>
  );
}
// React.memo로 메모이제이션하여 props가 변경되지 않으면 재렌더링 방지
export default memo(NurseRosterList);
